/* ============================================================================
   NWA native search drawer.

   Replaces Rebuy Smart Search. Two layers, deliberately separate:

     ENTITY LAYER  — matches what was typed against a brand + category dictionary
                     rendered into the page by snippets/nwa-search-drawer.liquid.
                     Runs on keystroke with NO network call, so the brand row and
                     category chips paint before any request returns. 55%+ of real
                     search volume is a brand name, so this is the hot path.

     PRODUCT LAYER — /search/suggest.json. Shopify's own predictive search, whose
                     semantic matching already relates misspellings and stylised
                     brand names to the real thing: "spider" returns Sp5der,
                     "bathing ape" returns Bape, "god speed" returns Godspeed.
                     (Search & Discovery synonyms were retired by Shopify in
                     Sep 2026; this matching replaced them and needs no upkeep.)
                     Anything the dictionary cannot resolve falls through here,
                     and the brand is then BACK-INFERRED from the vendor of the
                     results.

   Category chips come from the Storefront API rather than a generated index, so
   they can never disagree with the collection pages. Chip hrefs are the SAME URLs
   the collection filter bar builds (?filter.p.product_type=...), which is the whole
   point: search and filters cannot drift because they are the same destination.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.NWA_SEARCH || {};
  var BRANDS = cfg.brands || [];
  /* ------------------------------------------------------------------------
     Category dictionary.

     productType only carries ten meaningful values, and they are NOT the words
     customers type: the catalogue says "T-Shirts", the search log says "tees";
     it says "Pants", they type "jeans" and "sweatpants"; it says "Sneakers",
     they type "shoes". Every alternate below was taken from the 90-day search
     log, with the search volume noted so it is obvious what each one is buying.

     `type` is the productType value used to build the filter URL, and must match
     the catalogue exactly. `handle` is the site-wide category collection.

     Terms with real volume and NO home in the catalogue are deliberately absent
     and fall through to product results: jorts (462), long sleeve (568),
     zip up (247), jackets (338), sleeveless (187), polo (152), slides (109).
     Giving those a productType is tracked in SEARCH-DATA-FIXES.md (P3).
     ---------------------------------------------------------------------- */
  var DEFAULT_CATS = [
    { title: 'T-Shirts',   type: 'T-Shirts',    handle: 't-shirts',
      alt: ['tees', 'tee', 't shirt', 'tshirt', 'tshirts', 'shirt', 'shirts'] },
    { title: 'Hoodies',    type: 'Hoodies',     handle: 'hoodies',
      alt: ['hoodie', 'hoody', 'sweatshirt', 'sweatshirts', 'crewneck'] },
    { title: 'Shorts',     type: 'Shorts',      handle: 'shorts',
      alt: ['short'] },
    { title: 'Pants',      type: 'Pants',       handle: 'pants-new',
      alt: ['jeans', 'jean', 'denim', 'sweatpants', 'sweats', 'joggers', 'trousers'] },
    { title: 'Sneakers',   type: 'Sneakers',    handle: 'all-sneakers',
      alt: ['shoes', 'shoe', 'sneaker', 'trainers'] },
    { title: 'Hats',       type: 'Hats',        handle: 'hats',
      alt: ['hat', 'cap', 'caps'] },
    { title: 'Beanies',    type: 'Beanies',     handle: 'beanies',
      alt: ['beanie'] },
    { title: 'Accessories', type: 'Accessories', handle: 'accessories',
      alt: ['socks', 'sock', 'belt', 'belts', 'bag', 'bags', 'sunglasses'] },
    { title: 'Kids',       type: 'KIDS',        handle: 'kids-clothing',
      alt: ['kid', 'kids', 'toddler', 'youth', 'childrens'] }
  ];

  /* ------------------------------------------------------------------------
     Colour dictionary.

     Colour is the standard Shopify taxonomy metafield shopify.color-pattern, and
     its storefront filter takes the TAXONOMY GID, not the label:
     ?filter.v.t.shopify.color-pattern=gid://shopify/TaxonomyValue/1 is Black.
     Passing "Black" returns zero results silently, which is why this map exists.
     The ids are Shopify's global taxonomy and are the same on every store.

     Colour is ~68% populated across the catalogue: apparel is 86-89% covered but
     Sneakers (86% missing) and Preowned (100% missing) are not, so a colour query
     under-reports on those two. Tracked in SEARCH-DATA-FIXES.md.
     ---------------------------------------------------------------------- */
  var COLOURS = [
    { title: 'Black', gid: 1 },   { title: 'Blue', gid: 2 },
    { title: 'White', gid: 3 },   { title: 'Gold', gid: 4 },
    { title: 'Silver', gid: 5 },  { title: 'Beige', gid: 6, alt: ['cream', 'tan'] },
    { title: 'Brown', gid: 7 },   { title: 'Gray', gid: 8, alt: ['grey', 'charcoal'] },
    { title: 'Green', gid: 9, alt: ['olive', 'camo'] },
    { title: 'Orange', gid: 10 }, { title: 'Pink', gid: 11 },
    { title: 'Purple', gid: 12 }, { title: 'Red', gid: 13, alt: ['burgundy', 'maroon'] },
    { title: 'Yellow', gid: 14 }, { title: 'Navy', gid: 15, alt: ['navy blue'] },
    { title: 'Multicolor', gid: 2865, alt: ['multi', 'multicolour'] }
  ];

  var CATS = cfg.categories || DEFAULT_CATS;
  var SF_TOKEN = cfg.storefrontToken;
  var SF_URL = '/api/2024-10/graphql.json';
  var DEBOUNCE = 120;
  var MIN_CHARS = 2;

  /* ---------- text helpers ---------------------------------------------- */

  function norm(s) {
    return (s || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9 ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* Space-insensitive form. Cheap, and it alone catches a large share of the real
     misspellings in the search logs: "god speed" -> godspeed, "hell star" ->
     hellstar, "saintvanity" -> saint vanity, "chrome heart" -> chromehearts. */
  function squash(s) { return norm(s).replace(/ /g, ''); }

  function levenshtein(a, b) {
    if (a === b) return 0;
    if (!a.length || !b.length) return Math.max(a.length, b.length);
    var prev = [], i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      var cur = [i];
      for (j = 1; j <= b.length; j++) {
        cur[j] = Math.min(
          prev[j] + 1,
          cur[j - 1] + 1,
          prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
        );
      }
      prev = cur;
    }
    return prev[b.length];
  }

  /* Tolerance scales with length so short brands do not collide with each other
     ("rta" and "rhude" must stay distinct), while long ones absorb a typo or two. */
  function tolerance(len) { return len <= 4 ? 0 : len <= 7 ? 1 : 2; }

  /* ---------- entity matching ------------------------------------------- */

  /* Ordered best-first. Exact beats prefix beats fuzzy, and a longer dictionary
     entry beats a shorter one so "saint vanity" wins over "saint michael" when
     the whole phrase was typed. */
  function matchEntities(q, dict, opts) {
    opts = opts || {};
    var nq = norm(q), sq = squash(q);
    if (nq.length < MIN_CHARS) return [];
    var out = [];
    for (var i = 0; i < dict.length; i++) {
      var e = dict[i];
      /* An entity matches on its title OR any of its alternates. Alternates carry
         the words customers actually type ("tees", "jeans", "shoes") which are not
         the words the catalogue uses ("T-Shirts", "Pants", "Sneakers"). */
      var names = [e.title].concat(e.alt || []);
      var best = null;
      for (var k = 0; k < names.length; k++) {
        var ne = norm(names[k]), se = squash(names[k]), rank = null;
        if (ne === nq || se === sq) rank = 0;
        else if (ne.indexOf(nq) === 0 || se.indexOf(sq) === 0) rank = 1;
        /* Rank 2 = the QUERY starts with the entity, i.e. there are extra words
           after it. Useful when matching a whole query in one go, but wrong when
           testing token spans: it would let the span "godspeed shorts" match the
           brand Godspeed and swallow "shorts", so the category never resolves.
           Span callers pass { whole: true } and the shorter span matches instead. */
        else if (!opts.whole && (nq.indexOf(ne + ' ') === 0 || sq.indexOf(se) === 0)) rank = 2;
        else if (ne.indexOf(' ' + nq) > -1 || ne.indexOf(nq + ' ') > -1) rank = 3;
        else if (sq.length >= 4 && levenshtein(sq, se) <= tolerance(sq.length)) rank = 4;
        if (rank !== null && (best === null || rank < best.rank)) {
          best = { entity: e, rank: rank, len: ne.length, count: e.count || 0 };
        }
      }
      if (best) out.push(best);
    }
    /* Same rank means the query is ambiguous — "saint" prefixes both SAINT VANITY
       and Saint Michael. Break the tie on catalogue size, because the bigger brand
       is overwhelmingly the one being asked for (Saint Vanity: 97 products and
       1,718 searches; Saint Michael: 17 and far fewer). The drawer still lists the
       runners-up, so the customer can pick the other one in a click. */
    out.sort(function (a, b) {
      return a.rank - b.rank || b.count - a.count || a.len - b.len;
    });
    return out.map(function (x) { return x.entity; });
  }

  /* Residual matching needs every word of a multi-word alternate removed too,
     so "godspeed long sleeve" leaves nothing dangling after the category match. */
  function entityWords(e) {
    return [e.title].concat(e.alt || []).map(norm);
  }

  /* Find the longest token span that resolves to an entity, and hand back the
     tokens it did not consume.

     Every contiguous span, not just the ends: a modifier can sit in front of the
     brand, as in "black godspeed hoodie", which leaves Godspeed in the middle.
     Queries are short — 97% of real ones are four tokens or fewer — so the extra
     spans are a handful of string compares against a 65-entry list, behind a
     debounce. Longest-first so "saint vanity" beats "saint". */
  function findEntity(tokens, dict) {
    for (var len = tokens.length; len >= 1; len--) {
      for (var start = 0; start + len <= tokens.length; start++) {
        var span = tokens.slice(start, start + len).join(' ');
        if (span.length < MIN_CHARS) continue;
        var hits = matchEntities(span, dict, { whole: true });
        if (hits.length) {
          return {
            entity: hits[0],
            rest: tokens.slice(0, start).concat(tokens.slice(start + len))
          };
        }
      }
    }
    return null;
  }

  /* Parse a query into { brand, category, residual }.

     BOTH lookups are span-based. Testing only the whole string was why "black
     hoodies" resolved to nothing: "hoodies" is in there, but the string as a
     whole matches no category, so the query fell through to relevance and the
     brand was then wrongly inferred from whichever label happened to dominate
     the results. Spans find the category and leave "black" as the residual.

     The residual matters downstream: when something is left over, the customer
     asked for more than the entity alone, so products must still come from a
     text search rather than from plain best sellers of the whole category. */
  function parseQuery(q) {
    var tokens = norm(q).split(' ').filter(Boolean);
    if (!tokens.length) return { brand: null, category: null, residual: '' };

    var bHit = findEntity(tokens, BRANDS);
    var brand = bHit ? bHit.entity : null;
    var rest = bHit ? bHit.rest : tokens;

    var cHit = findEntity(rest, CATS);
    var cat = cHit ? cHit.entity : null;
    if (cHit) rest = cHit.rest;

    /* Colour last, over whatever is still unclaimed. Recognising it matters even
       where it cannot be filtered: without this, "pink" resolved to nothing and
       the brand was then inferred from the results, so searching pink offered
       Righteous. A recognised colour blocks that inference outright. */
    var colHit = findEntity(rest, COLOURS);
    var colour = colHit ? colHit.entity : null;
    if (colHit) rest = colHit.rest;

    return {
      brand: brand, category: cat, colour: colour,
      residual: rest.join(' ')
    };
  }

  /* ---------- data fetching --------------------------------------------- */

  var facetCache = {};

  /* Facets for a collection: the product types inside it AND the brands inside
     it. One request answers both directions of the journey — "godspeed" wants
     the types Godspeed sells, "hoodie" wants the brands that sell hoodies. */
  function fetchFacets(handle) {
    if (facetCache[handle]) return Promise.resolve(facetCache[handle]);
    var empty = { types: [], vendors: [], vendor: null };
    if (!SF_TOKEN) return Promise.resolve(empty);
    var gql = '{ collection(handle:"' + handle + '"){ products(first:1){ filters{ id values{ label count } } } } }';
    return sfetch(gql)
      .then(function (j) {
        var col = j && j.data && j.data.collection;
        var filters = (col && col.products && col.products.filters) || [];
        var types = [], vendors = [];
        filters.forEach(function (f) {
          if (f.id === 'filter.p.product_type') {
            f.values.forEach(function (v) {
              if (v.count > 0) types.push({ label: v.label, count: v.count });
            });
          } else if (f.id === 'filter.p.vendor') {
            f.values.forEach(function (v) {
              if (v.count > 0) vendors.push({ label: v.label, count: v.count });
            });
          }
        });
        function byCount(a, b) { return b.count - a.count; }
        types.sort(byCount);
        vendors.sort(byCount);
        /* The exact vendor STRING, which is not the collection title: the
           collection is "SAINT VANITY" while the vendor is "Saint Vanity", and
           filter.p.vendor is case-sensitive — wrong case returns nothing. */
        var facets = {
          types: types,
          vendors: vendors,
          vendor: vendors.length === 1 ? vendors[0].label : (vendors[0] ? vendors[0].label : null)
        };
        facetCache[handle] = facets;
        return facets;
      })
      .catch(function () { return empty; });
  }

  var bestCache = {};

  /* Best sellers from a resolved destination. Once the query has resolved to a
     collection, relevance ranking has nothing left to add — the customer has
     told us the set they want, so show the ones that actually sell.

     @inContext pins the buyer's country so prices come back in their market
     currency rather than the shop default; the currencyCode travels with each
     amount so the symbol can never disagree with the number. */
  function fetchBestSellers(handle, filters) {
    var key = handle + '|' + JSON.stringify(filters || []);
    if (bestCache[key]) return Promise.resolve(bestCache[key]);
    if (!SF_TOKEN) return Promise.resolve([]);
    var country = (window.Shopify && window.Shopify.country) || 'US';
    var fArg = filters && filters.length ? ' filters: ' + toGraphQL(filters) : '';
    var gql = 'query @inContext(country: ' + country + ') {' +
      ' collection(handle:"' + handle + '"){' +
      '  products(first: 6, sortKey: BEST_SELLING' + fArg + '){' +
      '   edges{ node{ title handle vendor productType' +
      '    featuredImage{ url(transform:{maxWidth:112,maxHeight:112,crop:CENTER}) }' +
      '    priceRange{ minVariantPrice{ amount currencyCode } } } } } } }';
    return sfetch(gql)
      .then(function (j) {
        var col = j && j.data && j.data.collection;
        var edges = (col && col.products && col.products.edges) || [];
        var out = edges.map(function (e) {
          var n = e.node;
          var mv = n.priceRange && n.priceRange.minVariantPrice;
          return {
            title: n.title,
            url: '/products/' + n.handle,
            vendor: n.vendor,
            type: n.productType,
            featured_image: n.featuredImage ? { url: n.featuredImage.url } : null,
            price: mv ? mv.amount : null,
            currency: mv ? mv.currencyCode : null
          };
        });
        bestCache[key] = out;
        return out;
      })
      .catch(function () { return []; });
  }

  /* Filter inputs are GraphQL object literals, not JSON: the keys must be bare.
     JSON.stringify quotes them, which the API rejects. */
  function toGraphQL(filters) {
    /* Recursive: taxonomyMetafield nests an object, and JSON.stringify would
       quote its keys, which GraphQL rejects. */
    function lit(v) {
      if (v === null || typeof v !== 'object') return JSON.stringify(v);
      if (Array.isArray(v)) return '[' + v.map(lit).join(', ') + ']';
      return '{' + Object.keys(v).map(function (k) {
        return k + ': ' + lit(v[k]);
      }).join(', ') + '}';
    }
    return lit(filters);
  }

  function sfetch(gql) {
    return fetch(SF_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SF_TOKEN
      },
      body: JSON.stringify({ query: gql })
    }).then(function (r) { return r.json(); });
  }

  var suggestCache = {};

  function fetchProducts(q) {
    if (suggestCache[q]) return Promise.resolve(suggestCache[q]);
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=6' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,product_type,vendor,variants.title';
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var p = (j.resources && j.resources.results && j.resources.results.products) || [];
        suggestCache[q] = p;
        return p;
      })
      .catch(function () { return []; });
  }

  /* ---------- URL building ---------------------------------------------- */
  /* These must stay byte-identical to what the collection filter bar produces,
     or the two surfaces drift apart again. */

  function brandUrl(b) { return '/collections/' + b.handle; }

  function brandTypeUrl(b, type) {
    return '/collections/' + b.handle +
      '?filter.p.product_type=' + encodeURIComponent(type);
  }

  function catUrl(c) { return '/collections/' + c.handle; }

  function colourParam(colour) {
    return 'filter.v.t.shopify.color-pattern=' +
      encodeURIComponent('gid://shopify/TaxonomyValue/' + colour.gid);
  }

  /* Facets on the built-in all-products collection, so every facet shows as a
     REMOVABLE chip. Landing on /collections/hoodies?filter.p.vendor=X pins
     "hoodies" as the collection itself, which the shopper then cannot clear.

     Deliberately /collections/all and never /collections/shop-all: shop-all is a
     curated collection holding 3,295 of 5,483 products, so routing through it
     would silently hide 40% of the catalogue. */
  function allUrl(opts) {
    var qs = [];
    if (opts.type) qs.push('filter.p.product_type=' + encodeURIComponent(opts.type));
    if (opts.vendor) qs.push('filter.p.vendor=' + encodeURIComponent(opts.vendor));
    if (opts.colour) qs.push(colourParam(opts.colour));
    return '/collections/all' + (qs.length ? '?' + qs.join('&') : '');
  }

  /* The mirror of brandTypeUrl: stay in the category and narrow by brand. Same
     destination either way, but this keeps a category-first journey coherent. */
  function catVendorUrl(c, vendor) {
    return '/collections/' + c.handle +
      '?filter.p.vendor=' + encodeURIComponent(vendor);
  }

  function searchUrl(q, opts) {
    var u = '/search?q=' + encodeURIComponent(q);
    /* Native search is OR-matching: "saint vanity hoodie" returns 620 results
       because every hoodie in the catalogue matches "hoodie". Carrying the
       resolved facets onto the results page narrows that to 13 without giving
       up the full-results escape hatch. Measured on the live store. */
    if (opts && opts.vendor) u += '&filter.p.vendor=' + encodeURIComponent(opts.vendor);
    if (opts && opts.type) u += '&filter.p.product_type=' + encodeURIComponent(opts.type);
    if (opts && opts.colour) u += '&' + colourParam(opts.colour);
    return u;
  }

  window.NWASearchCore = {
    norm: norm,
    squash: squash,
    levenshtein: levenshtein,
    matchEntities: matchEntities,
    entityWords: entityWords,
    parseQuery: parseQuery,
    fetchFacets: fetchFacets,
    fetchBestSellers: fetchBestSellers,
    fetchProducts: fetchProducts,
    brandUrl: brandUrl,
    brandTypeUrl: brandTypeUrl,
    catUrl: catUrl,
    catVendorUrl: catVendorUrl,
    allUrl: allUrl,
    colourParam: colourParam,
    COLOURS: COLOURS,
    searchUrl: searchUrl,
    BRANDS: BRANDS,
    CATS: CATS,
    DEBOUNCE: DEBOUNCE,
    MIN_CHARS: MIN_CHARS
  };
})();
